import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import type {
  AggregatedOpenCollaboration,
  AggregatedTargetCollaboration,
  CollabAutomation,
  CollaborationRemovalResult,
  CollabTriggerType,
  ForVideosResponse,
  GroupMembersResponse,
  OpenCollaborationCreator,
  OpenCollaborationCreatorsData,
  OpenCollaborationCreatorsSortColumn,
  OpenCollaborationDetail,
  OpenCollaborationInsightsFunnel,
  OpenCollaborationInsightsPerformance,
  OpenCollaborationInsightsRelationships,
  OpenCollaborationInsightsSummary,
  OpenCollaborationListItem,
  OpenCollaborationsListResponse,
  OpenCollaborationsSortColumn,
  RemoveBulkResponse,
  TargetCollaborationCreator,
  TargetCollaborationCreatorsData,
  TargetCollaborationCreatorsSortColumn,
  TargetCollaborationDetail,
  TargetCollaborationGroupCreatorsData,
  TargetCollaborationGroupDetail,
  TargetCollaborationGroupedItem,
  TargetCollaborationGroupInsightsFunnel,
  TargetCollaborationGroupInsightsPerformance,
  TargetCollaborationGroupInsightsRelationships,
  TargetCollaborationGroupInsightsSummary,
  TargetCollaborationInsightsFunnel,
  TargetCollaborationInsightsPerformance,
  TargetCollaborationInsightsRelationships,
  TargetCollaborationInsightsSummary,
  TargetCollaborationListItem,
  TargetCollaborationMetric,
  TargetCollaborationMetricsResponse,
  TargetCollaborationProduct,
  TargetCollaborationSummary,
  TargetCollaborationsListResponse,
  TargetCollaborationsSortColumn,
  TargetGroupMembersPageResponse,
  VideoCollaborationOpenItem,
  VideoCollaborationTargetItem,
  VideoSelection,
} from "@unbox/api";
import { useCallback } from "react";
import { handleError } from "@/api/error";
import type { ChunkResponse } from "@/hooks/use-progressive-fetch";
import { useMerchant } from "@/providers/MerchantProvider";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { client, type MerchantScope, useScopedMerchantClient } from "./scoped-merchant-client";

// Re-export types for convenience
export type {
  CollaborationRemovalResult,
  OpenCollaborationCreator,
  OpenCollaborationCreatorsData,
  OpenCollaborationDetail,
  OpenCollaborationInsightsFunnel,
  OpenCollaborationInsightsPerformance,
  OpenCollaborationInsightsRelationships,
  OpenCollaborationInsightsSummary,
  OpenCollaborationListItem,
  TargetCollaborationCreator,
  TargetCollaborationCreatorsData,
  TargetCollaborationDetail,
  TargetCollaborationGroupCreatorsData,
  TargetCollaborationGroupDetail,
  TargetCollaborationGroupedItem,
  TargetCollaborationInsightsFunnel,
  TargetCollaborationInsightsPerformance,
  TargetCollaborationInsightsRelationships,
  TargetCollaborationInsightsSummary,
  TargetCollaborationListItem,
  TargetCollaborationMetric,
  TargetCollaborationProduct,
  TargetCollaborationSummary,
  TargetGroupMembersPageResponse,
  VideoCollaborationOpenItem,
  VideoCollaborationTargetItem,
};

// Re-export types from API for convenience
export type OpenCollaboration = AggregatedOpenCollaboration;
export type TargetCollaboration = AggregatedTargetCollaboration;
export type AggregatedCollaborationInfo = ForVideosResponse;
export type BulkRemoveResponse = RemoveBulkResponse;

/**
 * Fetch collaborations for a video selection.
 * Uses the DB-based endpoint that returns aggregated collaboration data.
 */
export const useVideoCollaborations = (selection: VideoSelection | null) => {
  const scope = useScopedMerchantClient();
  const enabled =
    !!scope.merchantId && selection !== null && (selection.mode === "exclude" || selection.videoIds.length > 0);

  return useQuery({
    queryKey: [
      "video-collaborations",
      scope.merchantId,
      scope.workspaceId,
      selection ? selectionQueryKey(selection) : "",
    ],
    queryFn: async (): Promise<AggregatedCollaborationInfo> => {
      if (!scope.merchantId || !selection) {
        return {
          openCollaborations: [],
          targetCollaborations: [],
          lastSyncedAt: null,
        };
      }

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations["for-videos"].$post({
            param: scope.param,
            json: { selection },
          })
        : await client.merchant[":merchantId"].collaborations["for-videos"].$post({
            param: scope.param,
            json: { selection },
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    enabled,
  });
};

const selectionQueryKey = (selection: VideoSelection): string => {
  if (selection.mode === "include") {
    return `include:${selection.videoIds.toSorted().join(",")}`;
  }
  const excludedPart = selection.excludedVideoIds.toSorted().join(",");
  const filterPart = JSON.stringify(selection.filters);
  return `exclude:${excludedPart}:${filterPart}`;
};

// Types for the bulk removal mutation payload
type RemovalRequest = {
  creatorId: string;
  openCollaborationIds: string[];
  targetCollaborationIds: string[];
};

type BulkRemoveFromCollaborationsPayload = {
  /** Selected open collaboration IDs */
  openCollaborationIds: string[];
  /** Selected target collaboration IDs */
  targetCollaborationIds: string[];
  /** Collaboration info from the hook (needed to know which creators are in which collaborations) */
  collaborationInfo: AggregatedCollaborationInfo;
};

/**
 * Remove creators from selected collaborations (bulk)
 * Uses the new DB-based bulk removal endpoint
 */
export const useBulkRemoveFromCollaborations = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: BulkRemoveFromCollaborationsPayload): Promise<BulkRemoveResponse> => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const { openCollaborationIds, targetCollaborationIds, collaborationInfo } = payload;

      // Build removal requests per creator
      const creatorRemovals = new Map<string, RemovalRequest>();

      // For each selected open collaboration, add its creators
      for (const collabId of openCollaborationIds) {
        const collab = collaborationInfo.openCollaborations.find((c) => c.collaborationId === collabId);
        if (collab) {
          for (const creatorId of collab.creatorIds) {
            const existing = creatorRemovals.get(creatorId) || {
              creatorId,
              openCollaborationIds: [],
              targetCollaborationIds: [],
            };
            existing.openCollaborationIds.push(collabId);
            creatorRemovals.set(creatorId, existing);
          }
        }
      }

      // For each selected target collaboration, add its creators
      for (const collabId of targetCollaborationIds) {
        const collab = collaborationInfo.targetCollaborations.find((c) => c.collaborationId === collabId);
        if (collab) {
          for (const creatorId of collab.creatorIds) {
            const existing = creatorRemovals.get(creatorId) || {
              creatorId,
              openCollaborationIds: [],
              targetCollaborationIds: [],
            };
            existing.targetCollaborationIds.push(collabId);
            creatorRemovals.set(creatorId, existing);
          }
        }
      }

      const removals = Array.from(creatorRemovals.values());

      if (removals.length === 0) {
        return {
          results: [],
          summary: { totalCreators: 0, totalRemoved: 0, totalFailed: 0 },
        };
      }

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations["remove-bulk"].$post({
            param: scope.param,
            json: { removals },
          })
        : await client.merchant[":merchantId"].collaborations["remove-bulk"].$post({
            param: scope.param,
            json: { removals },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      if (scope.merchantId) {
        // Invalidate collaboration cache
        queryClient.invalidateQueries({ queryKey: ["video-collaborations", scope.merchantId] });
      }
    },
  });
};

/**
 * Fetch all target collaborations for the current merchant
 * @param creatorUsername - Optional username to filter out collaborations the creator has already been invited to
 * @param enabled - Whether the query should be enabled (should be controlled by modal open state)
 */
export const useTargetCollaborations = (creatorUsername?: string, enabled: boolean = true) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["target-collaborations", scope.merchantId, scope.workspaceId, creatorUsername],
    queryFn: async () => {
      if (!scope.merchantId) return [];

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].tts["target-collaborations"].$get({
            param: scope.param,
            query: creatorUsername ? { creatorUsername } : {},
          })
        : await client.merchant[":merchantId"].tts["target-collaborations"].$get({
            param: scope.param,
            query: creatorUsername ? { creatorUsername } : {},
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      const data = await res.json();
      return data.collaborations;
    },
    enabled: !!scope.merchantId && enabled,
  });
};

/**
 * Add a creator to a target collaboration
 */
export const useAddCreatorToTargetCollaboration = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: {
      targetCollaborationId: string;
      creatorOpenId: string;
      sendDM?: boolean;
      // When set, the server records a `send_creator_invite` NBA interaction for this creator
      // against the resulting collab id — avoids a separate POST to /recommendations/invite from
      // the FE (which used to race the publish migration tx for big batches).
      nbaModelVersion?: string;
    }) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].tts["target-collaborations"][
            "add-creator"
          ].$post({
            param: scope.param,
            json: payload,
          })
        : await client.merchant[":merchantId"].tts["target-collaborations"]["add-creator"].$post({
            param: scope.param,
            json: payload,
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    onSuccess: () => {
      if (scope.merchantId) {
        queryClient.invalidateQueries({ queryKey: ["target-collaborations", scope.merchantId] });
        queryClient.invalidateQueries({ queryKey: ["target-collaborations-list", scope.merchantId] });
        queryClient.invalidateQueries({ queryKey: ["target-collaboration-detail", scope.merchantId] });
        queryClient.invalidateQueries({ queryKey: ["candidates", scope.merchantId] });
        queryClient.invalidateQueries({ queryKey: ["candidates-count", scope.merchantId] });
        queryClient.invalidateQueries({ queryKey: ["candidate", scope.merchantId] });
      }
    },
  });
};

// =============================================================================
// Collaboration List Hooks (for Collaborations Explorer page)
// =============================================================================

type ListOptions<Column extends string> = {
  search?: string;
  pagination: { limit: number; offset: number };
  sort?: { column: Column; direction: "asc" | "desc" };
  // UNB-1978: collabs never adopted into a Smart Collab Group (smart_collab_group_id IS NULL).
  ungroupedOnly?: boolean;
};

/**
 * Fetch target collaborations list with pagination and search.
 * Returns collaborations with Smart Unbox Grouping - identical collaborations
 * are automatically grouped together.
 */
export const useTargetCollaborationsList = (options: ListOptions<TargetCollaborationsSortColumn>) => {
  const scope = useScopedMerchantClient();
  const { search, pagination, sort, ungroupedOnly } = options;

  return useQuery({
    queryKey: [
      "target-collaborations-list",
      scope.merchantId,
      scope.workspaceId,
      search,
      pagination.limit,
      pagination.offset,
      sort?.column,
      sort?.direction,
      ungroupedOnly,
    ],
    queryFn: async (): Promise<TargetCollaborationsListResponse> => {
      if (!scope.merchantId) {
        return { collaborations: [], total: 0, totalCollaborations: 0 };
      }

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target.$get({
            param: scope.param,
            query: {
              search: search || undefined,
              limit: pagination.limit.toString(),
              offset: pagination.offset.toString(),
              column: sort?.column,
              direction: sort?.direction,
              includeTerminated: "true",
              ungroupedOnly: ungroupedOnly ? "true" : undefined,
            },
          })
        : await client.merchant[":merchantId"].collaborations.target.$get({
            param: scope.param,
            query: {
              search: search || undefined,
              limit: pagination.limit.toString(),
              offset: pagination.offset.toString(),
              column: sort?.column,
              direction: sort?.direction,
              includeTerminated: "true",
              ungroupedOnly: ungroupedOnly ? "true" : undefined,
            },
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

/**
 * Lazy-load the per-row metric counts (invitedCount, acceptedCount,
 * sampleRequestedCount, pendingPostsCount, postedVideosCount) for the visible
 * rows returned by `useTargetCollaborationsList`. The list endpoint omits
 * these so the table can paint immediately even when one row stands for a
 * 760-collab Smart Unbox group (UNB-1371).
 *
 * Pass an array of `{ key, collaborationIds }` pairs — `key` is the same row
 * key the table uses (groupId for group headers, collaborationId for everything
 * else) so the response can be looked up by row.
 */
type MetricRowInput = { key: string; collaborationIds: string[] };

/**
 * Server-side cap on `/target/metrics` (`rows.max(100)` in the route
 * validator). The frontend chunks any larger request into parallel batches
 * so infinite-scroll into a Smart Unbox group can keep growing the visible
 * row set without ever tripping the cap. Keep these in sync.
 */
const METRICS_BATCH_SIZE = 100;

export const useTargetCollaborationsMetrics = (rows: MetricRowInput[]) => {
  const merchantId = useMerchant();

  // Stable cache key: rows themselves can change reference on every render, but
  // the (key, collaborationIds[]) content only changes when the visible page
  // changes. Keying on this string keeps React Query from refetching on
  // unrelated re-renders (e.g. when expanding a Smart Unbox group).
  const cacheKey = rows
    .map((r) => `${r.key}:${[...r.collaborationIds].sort().join(",")}`)
    .sort()
    .join("|");

  return useQuery({
    queryKey: ["target-collaborations-metrics", merchantId, cacheKey],
    queryFn: async (): Promise<Record<string, TargetCollaborationMetric>> => {
      if (!merchantId || rows.length === 0) return {};

      const batches: MetricRowInput[][] = [];
      for (let i = 0; i < rows.length; i += METRICS_BATCH_SIZE) {
        batches.push(rows.slice(i, i + METRICS_BATCH_SIZE));
      }

      const responses = await Promise.all(
        batches.map(async (batch) => {
          const res = await client.merchant[":merchantId"].collaborations.target.metrics.$post({
            param: { merchantId },
            json: { rows: batch },
          });
          if (!res.ok) {
            throw await handleError(res);
          }
          return (await res.json()) as TargetCollaborationMetricsResponse;
        }),
      );

      const lookup: Record<string, TargetCollaborationMetric> = {};
      for (const data of responses) {
        for (const m of data.metrics) {
          lookup[m.key] = m;
        }
      }
      return lookup;
    },
    enabled: !!merchantId && rows.length > 0,
    // Always refetch on mount so the user never lands on a permanently-empty
    // view if a previous attempt errored (e.g. transient API restart). The
    // counts on this endpoint are cheap once the indexes are warm.
    refetchOnMount: "always",
    staleTime: 0,
    // Keep previously-fetched metrics visible while the next page loads, so
    // cells don't flicker back to skeletons during pagination.
    placeholderData: (previous) => previous,
  });
};

/**
 * Fetch individual collaborations for a specific group (for expansion).
 *
 * @deprecated Loads every collaboration in the group up-front, which is
 * unusable for groups with hundreds of members (UNB-1371). Prefer
 * `useGroupCollaborationsPaginated`. Kept for any existing call-sites.
 */
export const useGroupCollaborations = (collaborationIds: string[], enabled: boolean = true) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["group-collaborations", scope.merchantId, scope.workspaceId, [...collaborationIds].sort().join(",")],
    queryFn: async (): Promise<GroupMembersResponse> => {
      if (!scope.merchantId || collaborationIds.length === 0) {
        return { collaborations: [] };
      }

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target["group-members"].$post({
            param: scope.param,
            json: { collaborationIds, includeTerminated: true },
          })
        : await client.merchant[":merchantId"].collaborations.target["group-members"].$post({
            param: scope.param,
            json: { collaborationIds, includeTerminated: true },
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    enabled: !!scope.merchantId && collaborationIds.length > 0 && enabled,
  });
};

/**
 * Default page size for inline group expansion. The server caps `limit` at
 * 100; 50 is a balance between fewer round-trips and a snappy first paint
 * for the 760-collab Smart Unbox case described in UNB-1371.
 */
export const GROUP_MEMBERS_PAGE_SIZE = 50;

/**
 * Paginated, infinite-scroll variant of `useGroupCollaborations`. Each call
 * is bound to a single `groupId` so the parent component can mount one of
 * these per expanded Smart Unbox group without re-fetching siblings.
 *
 * The endpoint returns `null` for the per-row metric counts; the caller is
 * responsible for hydrating them via `useTargetCollaborationsMetrics`.
 */
export const useGroupCollaborationsPaginated = (groupId: string | null, enabled: boolean = true) => {
  const merchantId = useMerchant();

  return useInfiniteQuery({
    queryKey: ["group-collaborations-paginated", merchantId, groupId, GROUP_MEMBERS_PAGE_SIZE],
    queryFn: async ({ pageParam }): Promise<TargetGroupMembersPageResponse> => {
      if (!merchantId || !groupId) {
        return { collaborations: [], total: 0 };
      }

      const res = await client.merchant[":merchantId"].collaborations.target.group[":groupId"].members.$get({
        param: { merchantId, groupId },
        query: { limit: String(GROUP_MEMBERS_PAGE_SIZE), offset: String(pageParam) },
      });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    enabled: !!merchantId && !!groupId && enabled,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.collaborations.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });
};

/**
 * Fetch target collaborations count
 */
export const useTargetCollaborationsCount = (search?: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["target-collaborations-count", scope.merchantId, scope.workspaceId, search],
    queryFn: async () => {
      if (!scope.merchantId) return 0;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target.count.$get({
            param: scope.param,
            query: { search: search || undefined },
          })
        : await client.merchant[":merchantId"].collaborations.target.count.$get({
            param: scope.param,
            query: { search: search || undefined },
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      const data = await res.json();
      return data.total;
    },
    enabled: !!scope.merchantId,
  });
};

/**
 * Fetch open collaborations list with pagination and search
 */
export const useOpenCollaborationsList = (options: ListOptions<OpenCollaborationsSortColumn>) => {
  const scope = useScopedMerchantClient();
  const { search, pagination, sort } = options;

  return useQuery({
    queryKey: [
      "open-collaborations-list",
      scope.merchantId,
      scope.workspaceId,
      search,
      pagination.limit,
      pagination.offset,
      sort?.column,
      sort?.direction,
    ],
    queryFn: async (): Promise<OpenCollaborationsListResponse> => {
      if (!scope.merchantId) {
        return { collaborations: [], total: 0 };
      }

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.open.$get({
            param: scope.param,
            query: {
              search: search || undefined,
              limit: pagination.limit.toString(),
              offset: pagination.offset.toString(),
              column: sort?.column,
              direction: sort?.direction,
            },
          })
        : await client.merchant[":merchantId"].collaborations.open.$get({
            param: scope.param,
            query: {
              search: search || undefined,
              limit: pagination.limit.toString(),
              offset: pagination.offset.toString(),
              column: sort?.column,
              direction: sort?.direction,
            },
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

/**
 * Fetch open collaborations count
 */
export const useOpenCollaborationsCount = (search?: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["open-collaborations-count", scope.merchantId, scope.workspaceId, search],
    queryFn: async () => {
      if (!scope.merchantId) return 0;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.open.count.$get({
            param: scope.param,
            query: { search: search || undefined },
          })
        : await client.merchant[":merchantId"].collaborations.open.count.$get({
            param: scope.param,
            query: { search: search || undefined },
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      const data = await res.json();
      return data.total;
    },
    enabled: !!scope.merchantId,
  });
};

// =============================================================================
// Target Collaboration Detail Hook
// =============================================================================

/**
 * Fetch detailed information about a specific target collaboration.
 */
export const useTargetCollaborationDetail = (collaborationId: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["target-collaboration-detail", scope.merchantId, scope.workspaceId, collaborationId],
    queryFn: async (): Promise<TargetCollaborationDetail | null> => {
      if (!scope.merchantId || !collaborationId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target[":collaborationId"].$get(
            {
              param: { ...scope.param, collaborationId },
            },
          )
        : await client.merchant[":merchantId"].collaborations.target[":collaborationId"].$get({
            param: { ...scope.param, collaborationId },
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    enabled: !!scope.merchantId && !!collaborationId,
  });
};

// =============================================================================
// Collaboration Insights Panel Hooks
// =============================================================================

export const useTargetCollaborationInsightsSummary = (collaborationId: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["target-collaboration-insights-summary", scope.merchantId, scope.workspaceId, collaborationId],
    queryFn: async (): Promise<TargetCollaborationInsightsSummary | null> => {
      if (!scope.merchantId || !collaborationId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target[
            ":collaborationId"
          ].insights.summary.$get({ param: { ...scope.param, collaborationId } })
        : await client.merchant[":merchantId"].collaborations.target[":collaborationId"].insights.summary.$get({
            param: { ...scope.param, collaborationId },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!collaborationId,
  });
};

export const useTargetCollaborationInsightsFunnel = (collaborationId: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["target-collaboration-insights-funnel", scope.merchantId, scope.workspaceId, collaborationId],
    queryFn: async (): Promise<TargetCollaborationInsightsFunnel | null> => {
      if (!scope.merchantId || !collaborationId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target[
            ":collaborationId"
          ].insights.funnel.$get({ param: { ...scope.param, collaborationId } })
        : await client.merchant[":merchantId"].collaborations.target[":collaborationId"].insights.funnel.$get({
            param: { ...scope.param, collaborationId },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!collaborationId,
  });
};

export const useTargetCollaborationInsightsRelationships = (collaborationId: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["target-collaboration-insights-relationships", scope.merchantId, scope.workspaceId, collaborationId],
    queryFn: async (): Promise<TargetCollaborationInsightsRelationships | null> => {
      if (!scope.merchantId || !collaborationId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target[
            ":collaborationId"
          ].insights.relationships.$get({ param: { ...scope.param, collaborationId } })
        : await client.merchant[":merchantId"].collaborations.target[":collaborationId"].insights.relationships.$get({
            param: { ...scope.param, collaborationId },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!collaborationId,
  });
};

export const useTargetCollaborationInsightsPerformance = (
  collaborationId: string | undefined,
  mode: "invited" | "accepted" = "accepted",
) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["target-collaboration-insights-performance", scope.merchantId, scope.workspaceId, collaborationId, mode],
    queryFn: async (): Promise<TargetCollaborationInsightsPerformance | null> => {
      if (!scope.merchantId || !collaborationId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target[
            ":collaborationId"
          ].insights.performance.$get({ param: { ...scope.param, collaborationId }, query: { mode } })
        : await client.merchant[":merchantId"].collaborations.target[":collaborationId"].insights.performance.$get({
            param: { ...scope.param, collaborationId },
            query: { mode },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!collaborationId,
  });
};

// =============================================================================
// Target Collaboration Group Insights Panel Hooks
// =============================================================================

export const useTargetCollaborationGroupInsightsSummary = (groupId: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["target-collaboration-group-insights-summary", scope.merchantId, scope.workspaceId, groupId],
    queryFn: async (): Promise<TargetCollaborationGroupInsightsSummary | null> => {
      if (!scope.merchantId || !groupId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target.group[
            ":groupId"
          ].insights.summary.$get({ param: { ...scope.param, groupId } })
        : await client.merchant[":merchantId"].collaborations.target.group[":groupId"].insights.summary.$get({
            param: { ...scope.param, groupId },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!groupId,
  });
};

export const useTargetCollaborationGroupInsightsFunnel = (groupId: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["target-collaboration-group-insights-funnel", scope.merchantId, scope.workspaceId, groupId],
    queryFn: async (): Promise<TargetCollaborationGroupInsightsFunnel | null> => {
      if (!scope.merchantId || !groupId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target.group[
            ":groupId"
          ].insights.funnel.$get({ param: { ...scope.param, groupId } })
        : await client.merchant[":merchantId"].collaborations.target.group[":groupId"].insights.funnel.$get({
            param: { ...scope.param, groupId },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!groupId,
  });
};

export const useTargetCollaborationGroupInsightsRelationships = (groupId: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["target-collaboration-group-insights-relationships", scope.merchantId, scope.workspaceId, groupId],
    queryFn: async (): Promise<TargetCollaborationGroupInsightsRelationships | null> => {
      if (!scope.merchantId || !groupId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target.group[
            ":groupId"
          ].insights.relationships.$get({ param: { ...scope.param, groupId } })
        : await client.merchant[":merchantId"].collaborations.target.group[":groupId"].insights.relationships.$get({
            param: { ...scope.param, groupId },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!groupId,
  });
};

export const useTargetCollaborationGroupInsightsPerformance = (
  groupId: string | undefined,
  mode: "invited" | "accepted" = "accepted",
) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["target-collaboration-group-insights-performance", scope.merchantId, scope.workspaceId, groupId, mode],
    queryFn: async (): Promise<TargetCollaborationGroupInsightsPerformance | null> => {
      if (!scope.merchantId || !groupId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target.group[
            ":groupId"
          ].insights.performance.$get({ param: { ...scope.param, groupId }, query: { mode } })
        : await client.merchant[":merchantId"].collaborations.target.group[":groupId"].insights.performance.$get({
            param: { ...scope.param, groupId },
            query: { mode },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!groupId,
  });
};

// =============================================================================
// Open Collaboration Insights Panel Hooks
// =============================================================================

export const useOpenCollaborationInsightsSummary = (collaborationId: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["open-collaboration-insights-summary", scope.merchantId, scope.workspaceId, collaborationId],
    queryFn: async (): Promise<OpenCollaborationInsightsSummary | null> => {
      if (!scope.merchantId || !collaborationId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.open[
            ":collaborationId"
          ].insights.summary.$get({ param: { ...scope.param, collaborationId } })
        : await client.merchant[":merchantId"].collaborations.open[":collaborationId"].insights.summary.$get({
            param: { ...scope.param, collaborationId },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!collaborationId,
  });
};

export const useOpenCollaborationInsightsFunnel = (collaborationId: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["open-collaboration-insights-funnel", scope.merchantId, scope.workspaceId, collaborationId],
    queryFn: async (): Promise<OpenCollaborationInsightsFunnel | null> => {
      if (!scope.merchantId || !collaborationId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.open[
            ":collaborationId"
          ].insights.funnel.$get({ param: { ...scope.param, collaborationId } })
        : await client.merchant[":merchantId"].collaborations.open[":collaborationId"].insights.funnel.$get({
            param: { ...scope.param, collaborationId },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!collaborationId,
  });
};

export const useOpenCollaborationInsightsRelationships = (collaborationId: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["open-collaboration-insights-relationships", scope.merchantId, scope.workspaceId, collaborationId],
    queryFn: async (): Promise<OpenCollaborationInsightsRelationships | null> => {
      if (!scope.merchantId || !collaborationId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.open[
            ":collaborationId"
          ].insights.relationships.$get({ param: { ...scope.param, collaborationId } })
        : await client.merchant[":merchantId"].collaborations.open[":collaborationId"].insights.relationships.$get({
            param: { ...scope.param, collaborationId },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!collaborationId,
  });
};

export const useOpenCollaborationInsightsPerformance = (
  collaborationId: string | undefined,
  mode: "invited" | "accepted" = "accepted",
) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["open-collaboration-insights-performance", scope.merchantId, scope.workspaceId, collaborationId, mode],
    queryFn: async (): Promise<OpenCollaborationInsightsPerformance | null> => {
      if (!scope.merchantId || !collaborationId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.open[
            ":collaborationId"
          ].insights.performance.$get({ param: { ...scope.param, collaborationId }, query: { mode } })
        : await client.merchant[":merchantId"].collaborations.open[":collaborationId"].insights.performance.$get({
            param: { ...scope.param, collaborationId },
            query: { mode },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!collaborationId,
  });
};

// =============================================================================
// Target Collaboration Creators Hook
// =============================================================================

type CreatorStatusFilter =
  | "all"
  | "invited"
  | "sample_requested"
  | "sample_status"
  | "pending_post"
  | "posted_video"
  | "posted_no_gmv"
  | "cancelled";

type TargetCreatorsOptions = {
  status: CreatorStatusFilter;
  search?: string;
  pagination: { limit: number; offset: number };
  sort?: { column: TargetCollaborationCreatorsSortColumn; direction: "asc" | "desc" };
  relationship?: "3plus" | "1to2" | "first";
};

/**
 * Standalone fetcher for a single page of target-collaboration creators.
 * Extracted so the hook and the adjacent-tab prefetch can share the same function.
 */
export const fetchTargetCollaborationCreators = async (
  scope: MerchantScope,
  collaborationId: string,
  options: TargetCreatorsOptions,
): Promise<TargetCollaborationCreatorsData> => {
  const { status, search, pagination, sort, relationship } = options;
  const query = {
    status,
    search: search || undefined,
    limit: pagination.limit.toString(),
    offset: pagination.offset.toString(),
    column: sort?.column,
    direction: sort?.direction,
    relationship: relationship || undefined,
  };

  const res = scope.workspaceId
    ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target[
        ":collaborationId"
      ].creators.$get({
        param: { ...scope.param, collaborationId },
        query,
      })
    : await client.merchant[":merchantId"].collaborations.target[":collaborationId"].creators.$get({
        param: { ...scope.param, collaborationId },
        query,
      });

  if (!res.ok) {
    throw await handleError(res);
  }

  return res.json();
};

/** Build the canonical React Query key for target-collaboration creators. */
export const targetCollaborationCreatorsKey = (
  scope: MerchantScope,
  collaborationId: string,
  options: TargetCreatorsOptions,
) => [
  "target-collaboration-creators",
  scope.merchantId,
  scope.workspaceId,
  collaborationId,
  options.status,
  options.search,
  options.pagination.limit,
  options.pagination.offset,
  options.sort?.column,
  options.sort?.direction,
  options.relationship,
];

/**
 * Fetch paginated, filtered creators for a specific target collaboration.
 */
export const useTargetCollaborationCreators = (collaborationId: string | undefined, options: TargetCreatorsOptions) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: targetCollaborationCreatorsKey(scope, collaborationId ?? "", options),
    queryFn: async (): Promise<TargetCollaborationCreatorsData> => {
      if (!scope.merchantId || !collaborationId) {
        return { creators: [], total: 0 };
      }
      return fetchTargetCollaborationCreators(scope, collaborationId, options);
    },
    enabled: !!scope.merchantId && !!collaborationId,
  });
};

export type { CreatorStatusFilter };

// =============================================================================
// Target Collaboration Group Detail Hook
// =============================================================================

/**
 * Fetch detailed information about a target collaboration group.
 */
export const useTargetCollaborationGroupDetail = (groupId: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["target-collaboration-group-detail", scope.merchantId, scope.workspaceId, groupId],
    queryFn: async (): Promise<TargetCollaborationGroupDetail | null> => {
      if (!scope.merchantId || !groupId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target.group[":groupId"].$get({
            param: { ...scope.param, groupId },
          })
        : await client.merchant[":merchantId"].collaborations.target.group[":groupId"].$get({
            param: { ...scope.param, groupId },
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    enabled: !!scope.merchantId && !!groupId,
  });
};

// =============================================================================
// Target Collaboration Group Creators Hook
// =============================================================================

export const targetCollaborationGroupCreatorsKey = (
  scope: MerchantScope,
  groupId: string,
  options: Omit<TargetCreatorsOptions, "relationship">,
) => [
  "target-collaboration-group-creators",
  scope.merchantId,
  scope.workspaceId,
  groupId,
  options.status,
  options.search,
  options.pagination.limit,
  options.pagination.offset,
  options.sort?.column,
  options.sort?.direction,
];

export const fetchTargetCollaborationGroupCreators = async (
  scope: MerchantScope,
  groupId: string,
  options: Omit<TargetCreatorsOptions, "relationship">,
): Promise<TargetCollaborationGroupCreatorsData> => {
  const { status, search, pagination, sort } = options;
  const query = {
    status,
    search: search || undefined,
    limit: pagination.limit.toString(),
    offset: pagination.offset.toString(),
    column: sort?.column,
    direction: sort?.direction,
  };

  const res = scope.workspaceId
    ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target.group[
        ":groupId"
      ].creators.$get({
        param: { ...scope.param, groupId },
        query,
      })
    : await client.merchant[":merchantId"].collaborations.target.group[":groupId"].creators.$get({
        param: { ...scope.param, groupId },
        query,
      });

  if (!res.ok) {
    throw await handleError(res);
  }

  return await res.json();
};

/**
 * Fetch paginated, filtered creators for a target collaboration group.
 */
export const useTargetCollaborationGroupCreators = (groupId: string | undefined, options: TargetCreatorsOptions) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: targetCollaborationGroupCreatorsKey(scope, groupId ?? "", options),
    queryFn: async (): Promise<TargetCollaborationGroupCreatorsData> => {
      if (!scope.merchantId || !groupId) {
        return {
          creators: [],
          total: 0,
        };
      }
      return fetchTargetCollaborationGroupCreators(scope, groupId, options);
    },
    enabled: !!scope.merchantId && !!groupId,
  });
};

export type CollaborationCreatorRowsSource =
  | { kind: "group"; groupId: string; options: Omit<TargetCreatorsOptions, "pagination" | "relationship"> }
  | { kind: "target"; collaborationId: string; options: Omit<TargetCreatorsOptions, "pagination"> }
  | { kind: "open"; collaborationId: string; options: Omit<OpenCreatorsOptions, "pagination"> };

export const useCollaborationCreatorRowsFetcher = (source: CollaborationCreatorRowsSource) => {
  const scope = useScopedMerchantClient();

  return useCallback(
    async (
      offset: number,
      limit: number,
    ): Promise<ChunkResponse<TargetCollaborationCreator | OpenCollaborationCreator>> => {
      const pagination = { limit, offset };
      if (source.kind === "group") {
        const options = { ...source.options, pagination };
        const data = await queryClient.fetchQuery({
          queryKey: targetCollaborationGroupCreatorsKey(scope, source.groupId, options),
          queryFn: () => fetchTargetCollaborationGroupCreators(scope, source.groupId, options),
          staleTime: Number.POSITIVE_INFINITY,
        });
        return { items: data.creators, total: data.total };
      }
      if (source.kind === "target") {
        const options = { ...source.options, pagination };
        const data = await queryClient.fetchQuery({
          queryKey: targetCollaborationCreatorsKey(scope, source.collaborationId, options),
          queryFn: () => fetchTargetCollaborationCreators(scope, source.collaborationId, options),
          staleTime: Number.POSITIVE_INFINITY,
        });
        return { items: data.creators, total: data.total };
      }
      const options = { ...source.options, pagination };
      const data = await queryClient.fetchQuery({
        queryKey: openCollaborationCreatorsKey(scope, source.collaborationId, options),
        queryFn: () => fetchOpenCollaborationCreators(scope, source.collaborationId, options),
        staleTime: Number.POSITIVE_INFINITY,
      });
      return { items: data.creators, total: data.total };
    },
    [scope, source],
  );
};

// =============================================================================
// Open Collaboration Detail Hook
// =============================================================================

/**
 * Fetch detailed information about a specific open collaboration.
 */
export const useOpenCollaborationDetail = (collaborationId: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["open-collaboration-detail", scope.merchantId, scope.workspaceId, collaborationId],
    queryFn: async (): Promise<OpenCollaborationDetail | null> => {
      if (!scope.merchantId || !collaborationId) return null;

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.open[":collaborationId"].$get({
            param: { ...scope.param, collaborationId },
          })
        : await client.merchant[":merchantId"].collaborations.open[":collaborationId"].$get({
            param: { ...scope.param, collaborationId },
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    enabled: !!scope.merchantId && !!collaborationId,
  });
};

// =============================================================================
// Open Collaboration Creators Hook
// =============================================================================

type OpenCreatorStatusFilter =
  | "all"
  | "sample_requested"
  | "sample_status"
  | "pending_post"
  | "generated_gmv"
  | "posted_no_gmv"
  | "cancelled";

type OpenCreatorsOptions = {
  status: OpenCreatorStatusFilter;
  search?: string;
  pagination: { limit: number; offset: number };
  sort?: { column: OpenCollaborationCreatorsSortColumn; direction: "asc" | "desc" };
};

/**
 * Canonical React Query key for open collaboration creators.
 * Shared between the hook and the adjacent-tab prefetch in the detail page.
 */
export const openCollaborationCreatorsKey = (
  scope: MerchantScope,
  collaborationId: string,
  options: OpenCreatorsOptions,
) => [
  "open-collaboration-creators",
  scope.merchantId,
  scope.workspaceId,
  collaborationId,
  options.status,
  options.search,
  options.pagination.limit,
  options.pagination.offset,
  options.sort?.column,
  options.sort?.direction,
];

/**
 * Extracted so the hook and the adjacent-tab prefetch can share the same function.
 */
export const fetchOpenCollaborationCreators = async (
  scope: MerchantScope,
  collaborationId: string,
  options: OpenCreatorsOptions,
): Promise<OpenCollaborationCreatorsData> => {
  const { status, search, pagination, sort } = options;
  const query = {
    status,
    search: search || undefined,
    limit: pagination.limit.toString(),
    offset: pagination.offset.toString(),
    column: sort?.column,
    direction: sort?.direction,
  };

  const res = scope.workspaceId
    ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.open[
        ":collaborationId"
      ].creators.$get({
        param: { ...scope.param, collaborationId },
        query,
      })
    : await client.merchant[":merchantId"].collaborations.open[":collaborationId"].creators.$get({
        param: { ...scope.param, collaborationId },
        query,
      });

  if (!res.ok) {
    throw await handleError(res);
  }

  return await res.json();
};

/**
 * Fetch paginated, filtered creators for a specific open collaboration.
 */
export const useOpenCollaborationCreators = (collaborationId: string | undefined, options: OpenCreatorsOptions) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: openCollaborationCreatorsKey(scope, collaborationId ?? "", options),
    queryFn: async (): Promise<OpenCollaborationCreatorsData> => {
      if (!scope.merchantId || !collaborationId) return { creators: [], total: 0 };
      return fetchOpenCollaborationCreators(scope, collaborationId, options);
    },
    enabled: !!scope.merchantId && !!collaborationId,
  });
};

export type { OpenCreatorStatusFilter };

/**
 * Bulk add creators to a target collaboration
 */
export const useBulkAddCreatorsToTargetCollaboration = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: {
      targetCollaborationId: string;
      creatorOpenIds: string[];
      sendDM?: boolean;
      /** See useAddCreatorToTargetCollaboration.nbaModelVersion. */
      nbaModelVersion?: string;
    }): Promise<
      | { async: true; jobId: string }
      | {
          results: Array<{ creatorOpenId: string; success: boolean; reason?: string }>;
          summary: { total: number; succeeded: number; failed: number };
          publishedCollaborationId?: string;
        }
    > => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].tts["target-collaborations"][
            "bulk-add-creators"
          ].$post({
            param: scope.param,
            json: payload,
          })
        : await client.merchant[":merchantId"].tts["target-collaborations"]["bulk-add-creators"].$post({
            param: scope.param,
            json: payload,
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    onSuccess: () => {
      if (scope.merchantId) {
        queryClient.invalidateQueries({ queryKey: ["target-collaborations", scope.merchantId] });
        // Invalidate candidate caches to show updated invitation status
        queryClient.invalidateQueries({ queryKey: ["candidates", scope.merchantId] });
        queryClient.invalidateQueries({ queryKey: ["candidates-count", scope.merchantId] });
        queryClient.invalidateQueries({ queryKey: ["candidate", scope.merchantId] });
      }
    },
  });
};

type CreateTargetCollaborationPayload = {
  name: string;
  endTime: string;
  products: Array<{ id: string; targetCommissionRate: number; shopAdsCommissionRate?: number }>;
  sellerEmail: string;
  message?: string;
  freeSampleRule: {
    hasFreeSample: boolean;
    isSampleApprovalExempt: boolean;
  };
  minRequirements?: {
    minGmv?: number;
    minFollowers?: number;
    minPostRate?: number;
    minAvgViews?: number;
    categoryFit?: string;
  };
  automations?: Partial<Record<CollabTriggerType, AutomationOverrideInput>>;
};

type AutomationOverrideInput = { enabled: boolean; sequenceId: string | null };

export const useCreateTargetCollaboration = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: CreateTargetCollaborationPayload) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].tts["target-collaborations"].create.$post({
            param: scope.param,
            json: payload,
          })
        : await client.merchant[":merchantId"].tts["target-collaborations"].create.$post({
            param: scope.param,
            json: payload,
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    onSuccess: () => {
      if (scope.merchantId) {
        queryClient.invalidateQueries({ queryKey: ["target-collaborations", scope.merchantId] });
        queryClient.invalidateQueries({ queryKey: ["target-collaborations-list", scope.merchantId] });
        queryClient.invalidateQueries({ queryKey: ["target-collaborations-count", scope.merchantId] });
      }
    },
  });
};

type UpdateTargetCollaborationPayload = CreateTargetCollaborationPayload & {
  collaborationId: string;
};

export const useUpdateTargetCollaboration = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async ({ collaborationId, ...payload }: UpdateTargetCollaborationPayload) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].tts["target-collaborations"][
            ":collaborationId"
          ].$put({
            param: { ...scope.param, collaborationId },
            json: payload,
          })
        : await client.merchant[":merchantId"].tts["target-collaborations"][":collaborationId"].$put({
            param: { ...scope.param, collaborationId },
            json: payload,
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    onSuccess: (_, { collaborationId }) => {
      if (scope.merchantId) {
        queryClient.invalidateQueries({ queryKey: ["target-collaborations", scope.merchantId] });
        queryClient.invalidateQueries({ queryKey: ["target-collaborations-list", scope.merchantId] });
        queryClient.invalidateQueries({ queryKey: ["target-collaborations-count", scope.merchantId] });
        queryClient.invalidateQueries({ queryKey: ["target-collaboration-detail", scope.merchantId] });
        queryClient.invalidateQueries({
          queryKey: ["collaboration-sequence-overrides", scope.merchantId, collaborationId],
        });
      }
    },
  });
};

// =============================================================================
// Sample Request Action Hooks
// =============================================================================

type SampleRequestActionPayload = {
  collaborationId: string;
  applicationId: string;
};

type SampleRejectReason = "NOT_MATCH" | "OFFLINE" | "OUT_OF_STOCK" | "OTHER";

type SampleRequestRejectPayload = SampleRequestActionPayload & {
  reason?: SampleRejectReason;
};

/**
 * Approve a creator's sample request for a target collaboration.
 * Calls TikTok API via backend to approve the sample application.
 */
export const useApproveSampleRequest = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: SampleRequestActionPayload): Promise<void> => {
      if (!scope.merchantId) throw new Error("No merchant selected");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target[":collaborationId"][
            "sample-requests"
          ][":applicationId"].approve.$post({
            param: {
              ...scope.param,
              collaborationId: payload.collaborationId,
              applicationId: payload.applicationId,
            },
          })
        : await client.merchant[":merchantId"].collaborations.target[":collaborationId"]["sample-requests"][
            ":applicationId"
          ].approve.$post({
            param: {
              ...scope.param,
              collaborationId: payload.collaborationId,
              applicationId: payload.applicationId,
            },
          });
      if (!res.ok) throw await handleError(res);
    },
    onSuccess: (_data, variables) => {
      if (scope.merchantId) {
        queryClient.invalidateQueries({
          queryKey: ["target-collaboration-creators", scope.merchantId, variables.collaborationId],
        });
        queryClient.invalidateQueries({
          queryKey: ["target-collaboration-detail", scope.merchantId, variables.collaborationId],
        });
        queryClient.invalidateQueries({
          queryKey: ["open-collaboration-creators", scope.merchantId, variables.collaborationId],
        });
        queryClient.invalidateQueries({
          queryKey: ["open-collaboration-detail", scope.merchantId, variables.collaborationId],
        });
        // Operations "Sample Requests Management" feed (all tabs/counts/stats).
        queryClient.invalidateQueries({ queryKey: ["sample-requests"] });
      }
    },
  });
};

export const useDeleteTargetCollaboration = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (collaborationId: string) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].tts["target-collaborations"][
            ":collaborationId"
          ].$delete({
            param: { ...scope.param, collaborationId },
          })
        : await client.merchant[":merchantId"].tts["target-collaborations"][":collaborationId"].$delete({
            param: { ...scope.param, collaborationId },
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    onSuccess: () => {
      if (scope.merchantId) {
        queryClient.invalidateQueries({ queryKey: ["target-collaborations", scope.merchantId] });
        queryClient.invalidateQueries({ queryKey: ["target-collaborations-list", scope.merchantId] });
        queryClient.invalidateQueries({ queryKey: ["target-collaborations-count", scope.merchantId] });
      }
    },
  });
};

/**
 * Reject a creator's sample request for a target collaboration.
 * Calls TikTok API via backend to reject the sample application.
 */
export const useDenySampleRequest = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: SampleRequestRejectPayload): Promise<void> => {
      if (!scope.merchantId) throw new Error("No merchant selected");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target[":collaborationId"][
            "sample-requests"
          ][":applicationId"].reject.$post({
            param: {
              ...scope.param,
              collaborationId: payload.collaborationId,
              applicationId: payload.applicationId,
            },
            json: { reason: payload.reason ?? "OTHER" },
          })
        : await client.merchant[":merchantId"].collaborations.target[":collaborationId"]["sample-requests"][
            ":applicationId"
          ].reject.$post({
            param: {
              ...scope.param,
              collaborationId: payload.collaborationId,
              applicationId: payload.applicationId,
            },
            json: { reason: payload.reason ?? "OTHER" },
          });
      if (!res.ok) throw await handleError(res);
    },
    onSuccess: (_data, variables) => {
      if (scope.merchantId) {
        queryClient.invalidateQueries({
          queryKey: ["target-collaboration-creators", scope.merchantId, variables.collaborationId],
        });
        queryClient.invalidateQueries({
          queryKey: ["target-collaboration-detail", scope.merchantId, variables.collaborationId],
        });
        queryClient.invalidateQueries({
          queryKey: ["open-collaboration-creators", scope.merchantId, variables.collaborationId],
        });
        queryClient.invalidateQueries({
          queryKey: ["open-collaboration-detail", scope.merchantId, variables.collaborationId],
        });
        // Operations "Sample Requests Management" feed (all tabs/counts/stats).
        queryClient.invalidateQueries({ queryKey: ["sample-requests"] });
      }
    },
  });
};

// --- Collaboration Sequence Overrides ---

type CollaborationSequenceOverride = {
  triggerType: string;
  enabled: boolean;
  sequenceId: string | null;
  sequenceName: string | null;
};

export const useCollaborationSequenceOverrides = (collaborationId: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["collaboration-sequence-overrides", scope.merchantId, scope.workspaceId, collaborationId],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations[
            ":collaborationId"
          ].sequences.$get({
            param: { ...scope.param, collaborationId },
          })
        : await client.merchant[":merchantId"].collaborations[":collaborationId"].sequences.$get({
            param: { ...scope.param, collaborationId },
          });
      if (!res.ok) throw await handleError(res);
      return (await res.json()) as CollaborationSequenceOverride[];
    },
    enabled: !!scope.merchantId && !!collaborationId,
  });
};

export const useUpsertCollaborationSequenceOverride = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async ({
      collaborationId,
      triggerType,
      enabled,
      sequenceId,
    }: {
      collaborationId: string;
      triggerType: string;
      enabled: boolean;
      sequenceId?: string | null;
    }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations[":collaborationId"].sequences[
            ":triggerType"
          ].$put({
            param: { ...scope.param, collaborationId, triggerType: triggerType as "collaboration_invite" },
            json: { enabled, sequenceId: sequenceId ?? null },
          })
        : await client.merchant[":merchantId"].collaborations[":collaborationId"].sequences[":triggerType"].$put({
            param: { ...scope.param, collaborationId, triggerType: triggerType as "collaboration_invite" },
            json: { enabled, sequenceId: sequenceId ?? null },
          });
      if (!res.ok) throw await handleError(res);
    },
    onSuccess: (_, { collaborationId }) => {
      queryClient.invalidateQueries({
        queryKey: ["collaboration-sequence-overrides", scope.merchantId, scope.workspaceId, collaborationId],
      });
      queryClient.invalidateQueries({ queryKey: ["collab-automations", scope.merchantId] });
    },
  });
};

export const useDeleteCollaborationSequenceOverride = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async ({ collaborationId, triggerType }: { collaborationId: string; triggerType: string }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations[":collaborationId"].sequences[
            ":triggerType"
          ].$delete({
            param: { ...scope.param, collaborationId, triggerType: triggerType as "collaboration_invite" },
          })
        : await client.merchant[":merchantId"].collaborations[":collaborationId"].sequences[":triggerType"].$delete({
            param: { ...scope.param, collaborationId, triggerType: triggerType as "collaboration_invite" },
          });
      if (!res.ok) throw await handleError(res);
    },
    onSuccess: (_, { collaborationId }) => {
      queryClient.invalidateQueries({
        queryKey: ["collaboration-sequence-overrides", scope.merchantId, scope.workspaceId, collaborationId],
      });
      queryClient.invalidateQueries({ queryKey: ["collab-automations", scope.merchantId] });
    },
  });
};

export const useTargetCollaborationAutomations = (
  collaborationId: string | undefined,
  opts?: { enabled?: boolean },
) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["collab-automations", scope.merchantId, scope.workspaceId, "collaboration", collaborationId],
    queryFn: async (): Promise<CollabAutomation[]> => {
      if (!collaborationId) throw new Error("No collaboration selected");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations[
            ":collaborationId"
          ].automations.$get({ param: { ...scope.param, collaborationId } })
        : await client.merchant[":merchantId"].collaborations[":collaborationId"].automations.$get({
            param: { ...scope.param, collaborationId },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: (opts?.enabled ?? true) && !!scope.merchantId && !!collaborationId,
  });
};
